import { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { trackPromise } from "react-promise-tracker";
import { useDispatch, useSelector } from "react-redux";
import _ from "lodash";
import { Box } from "@mui/material";
import {
  selectIsDrawerOpen,
  selectUserDetail,
  userDetail,
} from "./redux/slice/userSlice";
import LoginPage from "./main/Login/loginPage";
import Spinner from "./shared-components/spinner";
import apiClient from "./lib/api";
import urlConstant from "./constants/urlConstants";
import dataConstant from "./constants/dataConstants";
import Header from "./shared-components/header";
import SideBar from "./shared-components/sideBar";
import PageNotFound from "./main/404/pageNotFound";
import ClientIntakeFrom from "./main/ClientIntake/clientIntakeFrom";
import ClientIntakeSuccessPage from "./main/ClientIntake/clientIntakeSuccessPage";
import CacheBuster from "react-cache-buster";
import pkg from "../package.json";
import ForgotPasswordPage from "./main/ForgotPassword/forgotPasswordPage";
import ResetPasswordPage from "./main/ForgotPassword/resetPasswordForm";
import { adminRoute, marketerRoute, staffRoute } from "./components/route/routeConfig";
import RouteComponentMap from "./RouteComponentMap";
const version = pkg.version;

const headerTitleArray = [
  { path: "dashboard", title: "Dashboard" },
  { path: "submission", title: "New Client Submission" },
  { path: "managesubmission", title: "Manage Client Submission" },
  { path: "wellskyclients", title: "WellSky Clients" },
  { path: "clientdetail", title: "WellSky Client Detail" },
  { path: "profile", title: "My Profile" },
  { path: "change-password", title: "Change Password" },
  { path: "state-city", title: "State - City" },
  { path: "liaison", title: "Liaisons" },
  { path: "marketers", title: "Marketer's Dashboard" },
  { path: "users", title: "Users" },
  { path: "submissiondetail", title: "Submission Detail" },
];

const PrivateRoute = ({ Component }) => {
  const getIsDrawerOpen = useSelector(selectIsDrawerOpen);
  const location = useLocation();
  const currentPath = location.pathname;
  const getAllSegments = _.split(currentPath, "/").filter(Boolean);
  const getCurrentTitle = _.find(
    headerTitleArray,
    (x) => x.path === getAllSegments[0].toLowerCase()
  )?.title;
  return (
    <>
      <SideBar title={getCurrentTitle} />
      <Box
        display="flex"
        flexDirection="column"
        gap={3}
        height="100vh"
        width={{
          xs: "100%",
          sm: getIsDrawerOpen ? `calc(100% - 240px)` : "100%",
        }}
        ml={{
          xs: 0,
          sm: getIsDrawerOpen ? `240px` : 0,
        }}
        p={{ xs: 2, md: 3 }}
        zIndex={1100}
        position="relative"
      >
        <Header title={getCurrentTitle} />
         <Component />
      </Box>
    </>
  );
};

const isAdminSubdomain = (subdomain) => {
  const adminSubdomains =
    process.env.REACT_APP_ADMIN_SUBDOMAIN?.split(",").map((s) => s.trim()) ||
    [];
  return adminSubdomains.includes(subdomain);
};

function AppRoutes() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const selectUserDetails = useSelector(selectUserDetail);
  const location = useLocation();
  const [subdomain, setSubdomain] = useState("");
  const [userDetails, setUserDetails] = useState(null);

  const publicPaths = [
    { pathName: "/login", arguments: 1, argumentsTotal: 1 },
    { pathName: "/clientIntake", arguments: 1, argumentsTotal: 1 },
    { pathName: "/intakeSuccess", arguments: 1, argumentsTotal: 1 },
    { pathName: "/forgot-password", arguments: 1, argumentsTotal: 1 },
    { pathName: "/reset-password", arguments: 1, argumentsTotal: 1 },
  ];
  const publicRoutes = [
    { path: "/login", element: <LoginPage /> },
    { path: "/clientIntake", element: <ClientIntakeFrom /> },
    { path: "/intakeSuccess", element: <ClientIntakeSuccessPage /> },
    { path: "/forgot-password", element: <ForgotPasswordPage /> },
    { path: "/reset-password", element: <ResetPasswordPage /> },
  ];

  useEffect(() => {
    if (subdomain !== "") {
      const token = localStorage.getItem("accessToken");
      if (token) {
        if (!selectUserDetails?.email) {
          trackPromise(
            apiClient
              .get(`${urlConstant.Login.GetUserDetail}?token=${token}`, {
                authRequired: true,
              })
              .then((response) => {
                if (response.status === dataConstant.APIStatus.Failure) {
                  navigate("/login");
                  localStorage.removeItem("accessToken")
                } else {
                  const userDetails = {
                    email: response.data.email,
                    firstName: response.data.firstName,
                    lastName: response.data.lastName,
                    phone: response.data.phone,
                    roles: response.data.roles,
                    userId: response.data.userId,
                    profileImageName: response.data.profileImageName,
                  };
                  dispatch(userDetail({ userDetail: userDetails }));
                  setUserDetails(userDetails);
                }
              })
          );
        } else if (selectUserDetails?.email) {
          setUserDetails(selectUserDetails);
        }
      } else {
        if (isAdminSubdomain(subdomain)) {
          const currentPath = location.pathname;
          const getAllSegments = _.split(currentPath, "/").filter(Boolean);
          const firstSegment = "/" + getAllSegments[0];
          const isPublicPath = _.find(
            publicPaths,
            (item) =>
              item.pathName.toLowerCase() === firstSegment?.toLowerCase()
          );
          setUserDetails(null);
          if (
            isPublicPath === undefined ||
            (isPublicPath.arguments !== getAllSegments.length &&
              isPublicPath.argumentsTotal !== getAllSegments.length)
          ) {
            navigate("/login");
          }
        }
      }
    }
  }, [location.pathname, subdomain]);

  useEffect(() => {
    if (subdomain === "") {
      const hostname = window.location.hostname;
      const parts = hostname.split(".");
      setSubdomain(parts[0]);
    }
  }, []);
 
  return (
    <CacheBuster
      currentVersion={version}
      isEnabled={true} //If false, the library is disabled.
      isVerboseMode={false} //If true, the library writes verbose logs to console.
      loadingComponent={<Spinner />} //If not pass, nothing appears at the time of new version check.
      metaFileDirectory={"."} //If public assets are hosted somewhere other than root on your server.
    >
      <Spinner />

      {subdomain !== "" && (
        <Routes>
          {publicRoutes.map(({ path, element }) => {
            if (path === "/login" && !isAdminSubdomain(subdomain)) {
              return;
            }
            return <Route key={path} path={path} element={element} />;
          })}
          {isAdminSubdomain(subdomain) && userDetails !== null && (
             userDetails?.roles === dataConstant.roles.marketer ? <Route path="/" element={<Navigate to="/submission" replace />} /> : <Route path="/" element={<Navigate to="/dashboard" replace />} /> 
          )}
          {!isAdminSubdomain(subdomain) && (
            <Route path="/" element={<Navigate to="/clientIntake" replace />} />
          )}
          {userDetails !== null &&
            isAdminSubdomain(subdomain) &&
            userDetails.roles === "Admin" &&
            adminRoute.map((route) => {
              const Component = RouteComponentMap[route.component];
              if (Component !== undefined) {
                return(
                  <Route
                    key={route.path}
                    path={route.path}
                    element={<PrivateRoute Component={Component} />}
                  />
                )
              }
            })}

          {userDetails !== null &&
            isAdminSubdomain(subdomain) &&
            userDetails.roles === "Staff" &&
            staffRoute.map((route) => {
              const Component = RouteComponentMap[route.component];
              if (Component !== undefined) {
                return(
                  <Route
                    key={route.path}
                    path={route.path}
                    element={<PrivateRoute Component={Component} />}
                  />
                )
              }
            })}
          
          {userDetails !== null &&
            isAdminSubdomain(subdomain) &&
            userDetails.roles === "Marketer" &&
            marketerRoute.map((route) => {
              const Component = RouteComponentMap[route.component];
              if (Component !== undefined) {
                return(
                  <Route
                    key={route.path}
                    path={route.path}
                    element={<PrivateRoute Component={Component} />}
                  />
                )
              }
            })}
          {userDetails !== null && isAdminSubdomain(subdomain) && (
            <Route path="*" element={<PageNotFound />} />
          )}
          {!isAdminSubdomain(subdomain) && (
            <Route path="*" element={<PageNotFound />} />
          )}
        </Routes>
      )}
    </CacheBuster>
  );
}

function App() {
  return (
    <Router>
      <AppRoutes />
    </Router>
  );
}

export default App;